import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Tag,
  Plus,
  Trash2,
  Edit,
  Search,
  Calendar,
  Store,
  Package,
  Percent,
  Link as LinkIcon,
  X,
  Check
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";

const DISCOUNTERS = ["Aldi", "Lidl", "Penny", "Kaufland", "Rewe", "Edeka", "Netto"];

const DiscountOffersPage = () => {
  const [offers, setOffers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDiscounter, setFilterDiscounter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [offerToMatch, setOfferToMatch] = useState(null);

  const [formData, setFormData] = useState({
    product_name: "",
    ean: "",
    discounter: "",
    regular_price: "",
    offer_price: "",
    valid_from: new Date().toISOString().split("T")[0],
    valid_until: "",
    matched_product_id: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [offersRes, productsRes] = await Promise.all([
        axios.get(`${API}/discount-offers?active_only=false`),
        axios.get(`${API}/products`)
      ]);
      setOffers(offersRes.data.offers || []);
      setProducts(productsRes.data);
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        regular_price: parseFloat(formData.regular_price) || 0,
        offer_price: parseFloat(formData.offer_price) || 0,
        matched_product_id: formData.matched_product_id || null
      };

      if (editingOffer) {
        await axios.put(`${API}/discount-offers/${editingOffer.id}`, data);
        toast.success("Angebot aktualisiert");
      } else {
        await axios.post(`${API}/discount-offers`, data);
        toast.success("Angebot erstellt");
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Angebot wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/discount-offers/${id}`);
      toast.success("Angebot gelöscht");
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setFormData({
      product_name: offer.product_name,
      ean: offer.ean || "",
      discounter: offer.discounter,
      regular_price: offer.regular_price?.toString() || "",
      offer_price: offer.offer_price?.toString() || "",
      valid_from: offer.valid_from,
      valid_until: offer.valid_until,
      matched_product_id: offer.matched_product_id || "",
      notes: offer.notes || ""
    });
    setIsDialogOpen(true);
  };

  const handleMatch = async (productId) => {
    if (!offerToMatch) return;
    try {
      await axios.post(`${API}/discount-offers/match/${offerToMatch.id}/${productId}`);
      toast.success("Produkt verknüpft");
      setMatchDialogOpen(false);
      setOfferToMatch(null);
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Verknüpfen");
    }
  };

  const resetForm = () => {
    setEditingOffer(null);
    setFormData({
      product_name: "",
      ean: "",
      discounter: "",
      regular_price: "",
      offer_price: "",
      valid_from: new Date().toISOString().split("T")[0],
      valid_until: "",
      matched_product_id: "",
      notes: ""
    });
  };

  const openNewOfferDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const filteredOffers = offers.filter(offer => {
    const matchesDiscounter = filterDiscounter === "all" || offer.discounter === filterDiscounter;
    const matchesSearch = !searchTerm || 
      offer.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      offer.matched_product_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesDiscounter && matchesSearch;
  });

  const isExpired = (dateStr) => new Date(dateStr) < new Date();
  const isActive = (offer) => !isExpired(offer.valid_until) && offer.is_active;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="discount-offers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
            Discounter-Angebote
          </h1>
          <p className="text-slate-500 mt-1">
            Verfolgen Sie Angebote bei Aldi, Lidl & Co.
          </p>
        </div>
        <Button onClick={openNewOfferDialog} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="w-4 h-4 mr-2" />
          Neues Angebot
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Produkt suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterDiscounter} onValueChange={setFilterDiscounter}>
              <SelectTrigger className="w-full sm:w-48">
                <Store className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Discounter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Discounter</SelectItem>
                {DISCOUNTERS.map(d => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Offers List */}
      <div className="grid gap-4">
        {filteredOffers.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Keine Angebote gefunden</p>
              <Button onClick={openNewOfferDialog} variant="outline" className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Erstes Angebot anlegen
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredOffers.map((offer, index) => (
            <motion.div
              key={offer.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className={`border-slate-200 ${isExpired(offer.valid_until) ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-900">{offer.product_name}</h3>
                        <Badge variant={isActive(offer) ? "default" : "secondary"} className={isActive(offer) ? "bg-emerald-100 text-emerald-700" : ""}>
                          {isActive(offer) ? "Aktiv" : "Abgelaufen"}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <Badge variant="outline" className="gap-1">
                          <Store className="w-3 h-3" />
                          {offer.discounter}
                        </Badge>
                        <span className="text-sm text-slate-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(offer.valid_from).toLocaleDateString('de-DE')} - {new Date(offer.valid_until).toLocaleDateString('de-DE')}
                        </span>
                        {offer.ean && (
                          <span className="text-xs text-slate-400 font-mono">
                            EAN: {offer.ean}
                          </span>
                        )}
                      </div>

                      {offer.matched_product_name ? (
                        <div className="mt-2 flex items-center gap-2 text-sm">
                          <LinkIcon className="w-4 h-4 text-emerald-600" />
                          <span className="text-slate-600">Verknüpft mit:</span>
                          <span className="font-medium text-emerald-700">{offer.matched_product_name}</span>
                        </div>
                      ) : (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="mt-2 text-amber-600 hover:text-amber-700 p-0 h-auto"
                          onClick={() => { setOfferToMatch(offer); setMatchDialogOpen(true); }}
                        >
                          <LinkIcon className="w-4 h-4 mr-1" />
                          Mit eigenem Produkt verknüpfen
                        </Button>
                      )}
                    </div>

                    <div className="text-right flex flex-col items-end gap-2">
                      <div>
                        {offer.regular_price > 0 && (
                          <p className="text-sm text-slate-400 line-through">{offer.regular_price.toFixed(2)}€</p>
                        )}
                        <p className="text-2xl font-bold text-emerald-600">{offer.offer_price.toFixed(2)}€</p>
                        {offer.regular_price > 0 && (
                          <p className="text-xs text-emerald-600 flex items-center justify-end gap-1">
                            <Percent className="w-3 h-3" />
                            {Math.round((1 - offer.offer_price / offer.regular_price) * 100)}% Rabatt
                          </p>
                        )}
                      </div>
                      
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => handleEdit(offer)} className="h-8 w-8">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDelete(offer.id)} className="h-8 w-8 text-rose-600 hover:text-rose-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {offer.notes && (
                    <p className="text-sm text-slate-500 mt-2 pt-2 border-t">{offer.notes}</p>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingOffer ? "Angebot bearbeiten" : "Neues Angebot"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Produktname beim Discounter *</Label>
                <Input
                  value={formData.product_name}
                  onChange={(e) => setFormData({...formData, product_name: e.target.value})}
                  placeholder="z.B. Coca-Cola 1,5L"
                  required
                />
              </div>
              
              <div>
                <Label>Discounter *</Label>
                <Select value={formData.discounter} onValueChange={(v) => setFormData({...formData, discounter: v})} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {DISCOUNTERS.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>EAN (optional)</Label>
                <Input
                  value={formData.ean}
                  onChange={(e) => setFormData({...formData, ean: e.target.value})}
                  placeholder="4001234567890"
                />
              </div>

              <div>
                <Label>Normalpreis (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.regular_price}
                  onChange={(e) => setFormData({...formData, regular_price: e.target.value})}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label>Angebotspreis (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.offer_price}
                  onChange={(e) => setFormData({...formData, offer_price: e.target.value})}
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <Label>Gültig ab *</Label>
                <Input
                  type="date"
                  value={formData.valid_from}
                  onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                  required
                />
              </div>

              <div>
                <Label>Gültig bis *</Label>
                <Input
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                  required
                />
              </div>

              <div className="col-span-2">
                <Label>Mit eigenem Produkt verknüpfen</Label>
                <Select value={formData.matched_product_id || "none"} onValueChange={(v) => setFormData({...formData, matched_product_id: v === "none" ? "" : v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Produkt wählen (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Keine Verknüpfung</SelectItem>
                    {products.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label>Notizen</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Optionale Anmerkungen..."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                {editingOffer ? "Speichern" : "Anlegen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Match Product Dialog */}
      <Dialog open={matchDialogOpen} onOpenChange={setMatchDialogOpen}>
        <DialogContent className="max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Produkt verknüpfen</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 mb-4">
            Verknüpfen Sie "{offerToMatch?.product_name}" mit einem Ihrer Produkte:
          </p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {products.map(product => (
              <button
                key={product.id}
                onClick={() => handleMatch(product.id)}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 transition-colors text-left"
              >
                <div>
                  <p className="font-medium text-slate-900">{product.name}</p>
                  {product.ean && <p className="text-xs text-slate-400">EAN: {product.ean}</p>}
                </div>
                <p className="text-sm text-slate-500">{product.purchase_price_gross?.toFixed(2) || "0.00"}€</p>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMatchDialogOpen(false)}>
              Abbrechen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiscountOffersPage;
